import React, { useEffect, useRef, useState } from 'react';

/**
 * Windowing is ON by default. Set `REACT_APP_CHART_WINDOWING=false` to mount every
 * chart eagerly (the pre-windowing behaviour) — useful for A/B measuring the memory
 * impact of windowing against the exact same build.
 */
export const CHART_WINDOWING_ENABLED =
  process.env.REACT_APP_CHART_WINDOWING !== 'false';

export type LazyMountProps = {
  children: React.ReactNode;
  /**
   * Style for the placeholder/wrapper element. Reserve the chart's width/height
   * here so the page layout (and scroll position) stays stable while the chart
   * is unmounted.
   */
  style?: React.CSSProperties;
  className?: string;
  /** How far outside the viewport to mount/keep mounted. Default '400px'. */
  rootMargin?: string;
  /** When true, the children stay mounted after first becoming visible
   *  (defer-only). Default false → unmount again when scrolled far away. */
  once?: boolean;
};

/**
 * Windows expensive children (e.g. Highcharts charts) to the viewport.
 *
 * Renders a height-reserving placeholder and only mounts `children` while the
 * placeholder is near the viewport. With the default `once=false`, children
 * unmount when scrolled far off-screen — so the chart's `HighchartsReact`
 * destroys its instance, bounding live SVG/DOM to what's actually on screen.
 *
 * This is the fix for "too many charts mounted at once": pages like Correlation
 * and Zones render ~20+ charts; without windowing every one stays in the DOM.
 */
export const LazyMount: React.FC<LazyMountProps> = ({
  children,
  style,
  className,
  rootMargin = '400px',
  once = false
}) => {
  const ref = useRef<HTMLDivElement>(null);
  // Start mounted when windowing is disabled (A/B "before" state).
  const [inView, setInView] = useState(!CHART_WINDOWING_ENABLED);

  useEffect(() => {
    if (!CHART_WINDOWING_ENABLED) return;

    const el = ref.current;
    if (!el) return;

    // Environments without IntersectionObserver (e.g. older jsdom in tests):
    // render eagerly so nothing is hidden.
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          setInView(true);
        } else if (!once) {
          setInView(false);
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin, once]);

  return (
    <div ref={ref} style={style} className={className}>
      {inView ? children : null}
    </div>
  );
};

export default LazyMount;
